<template>
  <div class="flex flex-col items-center justify-center min-h-screen bg-background p-4">
    <div class="max-w-md w-full bg-card border border-border rounded-lg shadow-md p-6">
      <div class="flex flex-col items-center justify-center text-center">
        <div v-if="loading" class="mb-4">
          <div class="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary"></div>
        </div>
        <h1 class="text-2xl font-bold text-foreground mb-2">{{ title }}</h1>
        <p class="text-muted-foreground mb-6">{{ message }}</p>
        
        <button 
          v-if="!loading && error" 
          @click="goToLogin" 
          class="w-full bg-primary hover:bg-primary-dark text-primary-foreground px-6 py-3 rounded-lg font-medium transition duration-200">
          Return to Login
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import apiClient from '@/services/api';
import { redeemInviteCode } from '@/utils/inviteLink';
import authStore from '@/store/auth';

export default {
  name: 'LoginSuccess',
  setup() {
    const router = useRouter();
    const loading = ref(true);
    const error = ref(false);
    const title = ref('Processing Login...');
    const message = ref('Please wait while we complete your authentication');

    const goToLogin = () => {
      router.push('/login');
    };

    onMounted(async () => {
      try {
        // First check for token in URL query params (from server redirect)
        const urlSearchParams = new URLSearchParams(window.location.search);
        let token = urlSearchParams.get('token');

        // If not found, try checking in hash (for client-side redirects)
        if (!token) {
          const urlParams = new URLSearchParams(window.location.hash.substring(1));
          token = urlParams.get('token');
        }

        if (!token) {
          // No token in URL, authentication failed
          loading.value = false;
          error.value = true;
          title.value = 'Authentication Failed';
          message.value = 'No authentication token found. Please try again.';
          return;
        }

        // Set token in localStorage and API client
        localStorage.setItem('token', token);
        apiClient.defaults.headers.common['Authorization'] = `Token ${token}`;

        // A new Google signup from a campaign link gets OLJ jobs before we load the user.
        await redeemInviteCode(apiClient);

        // Get user information
        const response = await apiClient.get('/auth/user/');
        
        // Update auth store
        authStore.setAuth(true, response.data);

        // Update UI
        loading.value = false;
        title.value = 'Login Successful';
        message.value = 'You have been successfully authenticated!';

        // Redirect to stored path (from ?redirect= on the login page) or /dashboard
        const oauthRedirect = localStorage.getItem('oauth_redirect') || '/dashboard';
        localStorage.removeItem('oauth_redirect');
        localStorage.setItem('showOnboardingModal', 'true');
        setTimeout(() => {
          router.push(oauthRedirect);
        }, 1500);
      } catch (err) {
        console.error('Login Success Error:', err);
        
        // Handle error
        loading.value = false;
        error.value = true;
        title.value = 'Authentication Error';
        message.value = 'There was an error processing your login. Please try again.';
      }
    });

    return {
      loading,
      error,
      title,
      message,
      goToLogin
    };
  }
};
</script> 